import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HlsPlaybackService } from '@display-now/shared';
import { SharedModule } from '../../../shared/shared.module';
import { resolveVideoMp4PlaybackUrl, resolveVideoPlaybackUrl } from '../../utils/video-playback.util';

@Component({
  standalone: true,
  imports: [SharedModule],
  selector: 'app-video-viewer',
  templateUrl: './video-viewer.component.html',
  styleUrls: ['./video-viewer.component.css'],
})
export class VideoViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() source: string;
  @Input() poster: string;
  @Input() streamingUrl?: string;
  @Input() fileUrl?: string;
  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;

  private viewReady = false;
  private mp4FallbackUrl = '';
  private lastAttachKey = '';

  constructor(private readonly hlsPlayback: HlsPlaybackService) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.loadSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.viewReady && (changes['source'] || changes['streamingUrl'] || changes['fileUrl'])) {
      this.loadSource();
    }
  }

  ngOnDestroy(): void {
    const video = this.videoEl?.nativeElement;
    if (video) {
      this.hlsPlayback.detach(video);
    }
  }

  private loadSource(): void {
    const video = this.videoEl?.nativeElement;
    if (!video) {
      return;
    }

    const primary = this.resolvePrimaryUrl();
    this.mp4FallbackUrl = this.resolveMp4Url() ?? (!this.hlsPlayback.isHlsSource(primary) ? primary : '');

    if (!primary && !this.mp4FallbackUrl) {
      return;
    }

    const attachKey = `${primary}|${this.mp4FallbackUrl}`;
    if (attachKey === this.lastAttachKey) {
      return;
    }

    this.lastAttachKey = attachKey;
    this.hlsPlayback.attach(video, primary || this.mp4FallbackUrl, {
      fallbackUrl: this.mp4FallbackUrl || undefined,
    });
    void video.play().catch(() => undefined);
  }

  private resolvePrimaryUrl(): string {
    if (this.streamingUrl?.trim() && this.hlsPlayback.isHlsSource(this.streamingUrl)) {
      return this.streamingUrl;
    }

    if (this.source?.trim()) {
      if (this.hlsPlayback.isHlsSource(this.source)) {
        return this.source;
      }

      return this.source;
    }

    const fromVideo = resolveVideoPlaybackUrl({
      streamingUrl: this.streamingUrl,
      fileUrl: this.fileUrl,
    });

    return fromVideo ?? '';
  }

  private resolveMp4Url(): string {
    if (this.fileUrl?.trim() && !this.hlsPlayback.isHlsSource(this.fileUrl)) {
      return this.fileUrl;
    }

    if (this.source?.trim() && !this.hlsPlayback.isHlsSource(this.source)) {
      return this.source;
    }

    return resolveVideoMp4PlaybackUrl({
      streamingUrl: this.streamingUrl,
      fileUrl: this.fileUrl,
    }) ?? '';
  }
}
